import { analytics } from '@qbe/qal';
import { ErrorType, RTKError, RTKErrorData } from 'types/error';

import type { RootState } from '../../store';
import { hasProperty } from '../types';
import mapStore from './mapStore';
import { qalFormatSoftBusinessError, qalFormatTechnicalError } from './types';

type AllErrors = ErrorType | RTKErrorData | ErrorType[];
/**
 * Distribute the errors to various error events.
 * @param payload - Decline Reducer State.
 * @param state
 */
const errorController = (payload: ErrorType, state: RootState) => {
  const augmentedPayload = {
    ...payload,
    data: {
      ...mapStore(state),
    },
  };

  if (!augmentedPayload?.errorType)
    return analytics.trackTechnicalError(qalFormatTechnicalError(augmentedPayload));

  // Define Icon Search or Quick Search error and concatenate search type to the message.
  if (augmentedPayload?.searchType)
    augmentedPayload.message = `${augmentedPayload.searchType} - ${augmentedPayload.message}`;

  const { errorType } = augmentedPayload;
  switch (errorType) {
    case 'business':
      return analytics.trackBusinessError(qalFormatSoftBusinessError(augmentedPayload));
    case 'technical':
    case 'mulesoft':
      return analytics.trackTechnicalError(qalFormatTechnicalError(augmentedPayload));
    default:
      return analytics.trackTechnicalError(qalFormatTechnicalError(augmentedPayload));
  }
};

/**
 * Distribute the Array errors and fire it one by one.
 * @param {Array} errors Array errors from the store
 * @param state
 */
const setMultiErrorHandler = (errors: Array<ErrorType>, state: RootState) => {
  // Classify each error item into errorTypes.
  errors.forEach((error: ErrorType) => {
    const { errorType } = error;
    errorController(
      {
        ...error,
        errorType:
          // Default - undefined or non-business and technical.
          !errorType || !/business|technical|mulesoft/.test(errorType) ? 'default' : errorType,
      },
      state
    );
  });
};

/**
 * Handles errors specific to the retrieval of quotes by iterating through the array of RTK errors,
 * augmenting each error with additional details, and then delegating the error handling to the central error controller.
 *
 * @param {RTKErrorData} payload - The error payload containing an array of RTK errors related to quote retrieval.
 * @param {RootState} state - The application state at the time of the error.
 */
const retrieveQuoteErrorHandler = (payload: RTKErrorData, state: RootState) => {
  const { errors } = payload;

  errors.forEach((error: RTKError) => {
    const retrieveQuoteError = {
      errorType: 'technical',
      technicalErrorType: 'retrieve quote error',
      ...error,
    };
    errorController(retrieveQuoteError, state);
  });
};

/**
 * Handles generic errors by augmenting the provided payload with additional data from the application state
 * and then tracks the error using the analytics service.
 *
 * @param {AllErrors} payload - The error payload to be processed.
 * @param {RootState} state - The application state at the time of the error.
 * @returns {boolean} - Returns true if the error tracking is successful, false otherwise.
 */
const genericErrorHandler = (payload: AllErrors, state: RootState) => {
  const augmentedPayload = {
    ...payload,
    data: {
      ...mapStore(state),
    },
  };
  return analytics.trackTechnicalError(qalFormatTechnicalError(augmentedPayload));
};
/**
 * Push Error data to the dataLayer
 */
const trackError = (payload: AllErrors, state: RootState) => {
  if (!payload) return false;
  // Retrieve Quote Error
  if (hasProperty(payload, 'errors')) return retrieveQuoteErrorHandler(payload, state);
  // Multiple Errors are triggered.
  if (Array.isArray(payload)) return setMultiErrorHandler(payload, state);
  // Single Error straight to the error controller.
  if (hasProperty(payload, 'message')) return errorController(payload, state);

  // Generic Error which is not categorised as above
  return genericErrorHandler(payload, state);
};

export default trackError;
